(ns learner.utils
  (:require [cljs.pprint :refer [cl-format]]
            [potpuri.core :as pot]
            [kyber.utils :refer [idx->letters]]))

(defn update-in-with-seqs
  "'Updates' a value / values in a nested associative structure & persistent
  vector. If a vector is specified then the update will apply to all child
  associative structures"
  [leaf lens update-fn]
  (let [property (first lens)
        next-lens (rest lens)]
    (condp = (type property)
      Keyword
      (update leaf
              property
              #(update-in-with-seqs % next-lens update-fn))

      PersistentVector
      (->> leaf
           (map (fn [element]
                  (update-in-with-seqs element next-lens update-fn)))
           (vec))

      nil
      (update-fn leaf))))

(defn format-float
  "Properly formats float"
  ([number] (format-float number 2))
  ([number precision]
   (let [truncate (fn [x]
                    (if (neg? x)
                      (js/Math.ceil x)
                      (js/Math.floor x)))
         int-part (truncate number)
         dec-part (js/Math.abs (- number int-part))]
     (str (cl-format nil "~:d" int-part)
          (when-not (zero? dec-part)
            (cl-format nil (str "~0," precision "f") dec-part))))))

(defn- calc-percent-raw
  [total value]
  (* (/ value total) 100))

(defn calc-percent
  [total value]
  (.round js/Math (calc-percent-raw total value)))

(defn- calc-video-progress
  [{:keys [progress duration]}]
  (if (and (some? progress) (some? duration))
    (calc-percent-raw duration progress)
    0))

(defn calc-moment-percent
  [moment-time duration]
  (if (and (some? moment-time) (some? duration))
    (calc-percent-raw duration moment-time)
    0))

(defn get-history-progress
  [player-attributes activity]
  (let [{:keys [furthest-viewed-time]} @activity
        {:keys [duration]} @player-attributes]
    (calc-video-progress {:progress furthest-viewed-time
                          :duration duration})))

(defn get-video-progress
  [player-attributes]
  (let [{:keys [time duration]} @player-attributes]
    (if (js/Number.isNaN duration)
      0
      (calc-video-progress {:progress time
                            :duration duration}))))

(defn map-user-notes-to-moments
  "Attaches User notes to designed moments"
  [{:keys [user-notes moments]}]
  (let [moment-user-notes
        (->> user-notes
             (filter #(contains? #{:moment :annotation}
                                 (keyword (get-in % [:metadata :type]))))
             (group-by #(get-in % [:metadata :value :uuid])))]
    (map #(assoc % :user-note (get-in moment-user-notes [(:uuid %) 0]))
         moments)))

(defn- get-video-moments-stream
  [{:keys [user-notes
           moments
           furthest-viewed-time
           show-all-moments]}]
  (let [timeline-user-notes
        (->> user-notes
             (filter #(= (keyword (get-in % [:metadata :type])) :time))
             (map #(-> %
                       (assoc :type "timeline-user-note")
                       (assoc :display-at (get-in % [:metadata :value])))))

        moments-with-user-notes
        (map-user-notes-to-moments {:user-notes user-notes
                                    :moments moments})

        viewed-moments
        (filter #(<= (:display-at %)
                     (+ furthest-viewed-time 1))
                moments-with-user-notes)]

    (->> (if (true? show-all-moments)
           moments-with-user-notes
           viewed-moments)
         (conj timeline-user-notes)
         (flatten)
         (sort-by :display-at))))

(defn- sort-by-annotation-coords
  [{a-x1 :x1 a-y1 :y1 a-page-number :page-number}
   {b-x1 :x1 b-y1 :y1 b-page-number :page-number}]
  (cond
    ;; Page Number
    (< a-page-number b-page-number) true
    (> a-page-number b-page-number) false

    ;; y value in XFDF coords is reversed
    (> a-y1 b-y1) true
    (< a-y1 b-y1) false

    ;; x value
    (< a-x1 b-x1) true
    :else false))

(defn- get-pdf-moments-stream
  [{:keys [user-annotations
           user-notes
           moments
           furthest-viewed-page
           show-all-moments
           annotations-in-view]}]
  (let [visible-annotation-xfdf-ids
        (set (map :xfdf-id annotations-in-view))

        eligible-moments
        (into moments user-annotations)

        moments-with-user-notes
        (map-user-notes-to-moments {:user-notes user-notes
                                    :moments eligible-moments})

        viewed-moments
        (filter (fn [{:keys [page-number xfdf-id]}]
                  (or
                   ;; User has passed the moment
                   (<= page-number (+ furthest-viewed-page 0))
                   ;; Moment is visible to the user, but user may not have
                   ;; gotten to page
                   (contains? visible-annotation-xfdf-ids xfdf-id)))
                moments-with-user-notes)]

    (sort sort-by-annotation-coords
          (if (true? show-all-moments)
            moments-with-user-notes
            viewed-moments))))

(defn moments->moment-stream
  "Constructs the learning stream taking into acount, embeded notes, user notes,
  & furthest-viewed-time"
  [{:keys [plo-type] :as context}]

  (case plo-type
    "video"
    (get-video-moments-stream context)

    "pdf"
    (get-pdf-moments-stream context)

    (js/console.error "PLO Type is not supported")))

(defn label-question-choices
  [choices]
  (map-indexed (fn [idx choice]
                 (assoc choice
                        :letter
                        (idx->letters idx)))
               choices))

(defn add-moment-question-choices
  [moment]
  (update-in moment [:question :choices] label-question-choices))

(defn format-bytes
  ([bytes] (format-bytes bytes 2))
  ([bytes precision]
   (if (zero? bytes)
     "0 Bytes"
     (let [step 1024
           steps ["Bytes" "KB" "MB" "GB" "TB" "PB" "EB" "ZB" "YB"]
           calculated-step-index (js/Math.floor (/ (js/Math.log bytes)
                                                   (js/Math.log step)))
           conversion (/ bytes (js/Math.pow step calculated-step-index))]
       (str (format-float conversion precision)
            " "
            (get steps calculated-step-index))))))

(defn seconds->hms
  [seconds]
  (let [hours (js/Math.floor (/ seconds 3600))
        minutes (js/Math.floor (/ (- seconds (* hours 3600)) 60))
        seconds (- seconds (* hours 3600) (* minutes 60))]
    (pot/map-of hours minutes seconds)))

(defn get-by-uuid
  [coll uuid]
  (->> coll
       (filter #(= (:uuid %) uuid))
       (first)))

(defn question-answered?
 [question]
 (some? (get-in question [:results :learner-answer])))

(defn question-answered-correctly?
  [{results :results
    question-type :type}]
  (let [{:keys [learner-answer correct-answer]} results]
    (and (some? correct-answer)
         (condp = (keyword question-type)
           :single-answer
           (= (:uuid learner-answer) correct-answer)

           :multiple-answer
           (= (set correct-answer) (->> learner-answer (map :uuid) set))

           (= learner-answer correct-answer)))))

;; Activity Helpers

(defn show-activity-control-bar?
  "Determins if the activity control bar should be shown"
  [{:keys [activity
           player-attributes
           user-is-active
           user-is-hovering-control-bar]}]
  (let [{:keys [show-activity-splash
                show-learning-stream
                show-resource-library
                designed-moment-active
                plo-type]}
        activity

        {:keys [buffering player-state show-volume-controls]}
        player-attributes]

    (and (nil? designed-moment-active)
         (not show-activity-splash)
         (not show-resource-library)
         (not show-learning-stream)
         (= plo-type "video")
         (or (= player-state :pause)
             buffering
             user-is-active
             show-volume-controls
             user-is-hovering-control-bar))))

(defn ->signature-certificate-url
  "The signed-certificate download endpoint, shared by the CLP banner and the
  signature confirmation page."
  [{:keys [base-url curriculum-uuid token]}]
  (str base-url
       "/v1/curriculum/" curriculum-uuid
       "/download-signature-certificate"
       "?token=" token))

(defn ->learner-route
  [{:keys [curriculum-uuid
           activity-uuid
           questionnaire-type
           question-uuid
           questionnaire-uuid
           end-segment
           query]}]

  (cond-> ""
    (some? curriculum-uuid)
    (str "/curriculum/" curriculum-uuid)

    (some? activity-uuid)
    (str "/activity/" activity-uuid)

    (some? questionnaire-type)
    (str "/qn/" (if (= questionnaire-type "floating")
                  "questionnaire" questionnaire-type))

    (= questionnaire-type "floating")
    (str "/" questionnaire-uuid)

    (some? question-uuid)
    (str "/q/" question-uuid)

    (some? end-segment)
    (str "/" end-segment)

    (some? query)
    (str query)))

(defn unviewed-assigned-moment?
  [moments]
  (->> moments
       (filter :learning-objective)
       (remove :learner-engaged)
       (seq)
       (some?)))

(defn is-video-based-moment?
  [moment]
  (some? (:display-at moment)))

(defn is-pdf-based-moment?
  [moment]
  (some? (:page-number moment)))

(defn is-learner-annotation?
  [moment]
  (contains? #{"highlight" "text"} (:type moment)))

(defn can-pin-moment?
  [moment]
  (not (contains? #{"timeline-user-note"
                    "highlight"
                    "text"} (:type moment))))

(defn get-home-url
  "Sends the user back to the proper homepage - hompage-url is set by
  a provider if signed in via one of the supported SSO methods."
  [{:keys [homepage-url clp-enabled curriculum-uuid activity-uuid]}]
  (cond
    (some? homepage-url)
    homepage-url

    (true? clp-enabled)
    (->learner-route
     {:curriculum-uuid curriculum-uuid})

    :else
    (->learner-route
     {:curriculum-uuid curriculum-uuid
      :activity-uuid activity-uuid})))

(defn format-page-count-label
  [page-count]
  (cond-> (str page-count" Page")
    (not= page-count 1)
    (str "s")))

(defn pdf-learning-stream->closest-card
  "Returns the closest card to a given page along with it's
  delta (distance) from that card."
  [stream page-number]
  (reduce
   (fn [{:keys [closest-page-delta] :as acc}
       {current-page :page-number}]
     (let [delta (js-invoke js/Math "abs" (- page-number current-page))]
       (if (or (nil? closest-page-delta)
               (< delta closest-page-delta))
         {:closest-page current-page
          :closest-page-delta delta}
         acc)))
   {:closest-page nil
    :closest-page-delta nil}
   stream))

(defn highlighted-resource->player-key
  "Returns the player-key for a given highlighted resource"
  [{resource-uuid :uuid}]
  (keyword (str "highlighted-resource_" resource-uuid)))
